'use client';

import React from 'react';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineRight } from '@/app/components/icons/BaseLineRight';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { useIsScreen } from '@/app/hooks';

interface BreadcrumbItem {
  id: string;
  name: string;
}

interface BreadcrumbProps {
  breadcrumbs: BreadcrumbItem[];
  onNavigate: (folderId: string) => void;
}

export function Breadcrumb({ breadcrumbs, onNavigate }: BreadcrumbProps) {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [hoverTimeout, setHoverTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const { isLgScreen } = useIsScreen();
  const maxLevels = isLgScreen ? 5 : 6;

  // 处理深层级显示
  const getDisplayBreadcrumbs = () => {
    if (breadcrumbs.length <= maxLevels) {
      return {
        displayItems: breadcrumbs,
        hiddenItems: [],
      };
    }

    // 如果层级过深，保留第一个（根目录）、最后几个，中间用省略号
    const first = breadcrumbs[0];
    const last = breadcrumbs.slice(-(maxLevels - 2)); // 减去首个和省略号占位
    const hidden = breadcrumbs.slice(1, -(maxLevels - 2)); // 被省略的项目

    return {
      displayItems: [
        first,
        { id: 'ellipsis', name: '...' }, // 省略号占位符
        ...last,
      ],
      hiddenItems: hidden,
    };
  };

  const { displayItems, hiddenItems } = getDisplayBreadcrumbs();

  const handleMouseEnter = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200); // 200ms 延迟避免鼠标快速移动时闪烁
    setHoverTimeout(timeout);
  };

  // 清理 timeout
  React.useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  return (
    <div className="flex items-center overflow-hidden text-xs">
      {displayItems.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && <BaseLineRight className="mx-1 flex-shrink-0 text-base text-system-white36" />}

          {item.id === 'ellipsis' ? (
            <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger
                className="flex items-center rounded-4 px-2 py-1 text-system-white64 transition-colors hover:text-system-white"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <BaseLineMore className="text-base" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {hiddenItems.map((hiddenItem) => (
                  <DropdownMenuItem
                    key={hiddenItem.id}
                    onClick={() => onNavigate(hiddenItem.id)}
                    className="max-w-56 cursor-pointer"
                  >
                    <CustomTooltip
                      content={hiddenItem.name}
                      showOnlyOnOverflow={true}
                      maxLines={1}
                      contentClassName="text-xs"
                    >
                      <span>{hiddenItem.name}</span>
                    </CustomTooltip>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <CustomTooltip content={item.name} contentClassName="text-xs">
              <button
                onClick={() => onNavigate(item.id)}
                disabled={index === displayItems.length - 1}
                className={`max-w-32 truncate transition-colors disabled:cursor-default lg:max-w-[104px] ${
                  index === displayItems.length - 1
                    ? 'font-semibold text-system-white'
                    : 'text-system-white64 hover:text-system-white'
                }`}
              >
                {item.name}
              </button>
            </CustomTooltip>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
